import React, { useState } from 'react';
import { User, ChevronDown, Download, RotateCcw, Mail, Crown, LogOut, Phone } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { ManageEmailTemplatesModal } from '../crm/ManageEmailTemplatesModal';
import { DataRestoreModal } from '../reminders/DataRestoreModal';
import { useSuperAdmin } from '../../hooks/useSuperAdmin';
import { useAdmin } from '../../hooks/useAdmin';
import { SaveMobileNumberModal } from '../admin/SaveMobileNumberModal';

interface UserProfileSectionProps {
  collapsed: boolean;
  onBackupData: () => void;
  onSignOut: () => void;
}

export function UserProfileSection({ collapsed, onBackupData, onSignOut }: UserProfileSectionProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [showEmailTemplatesModal, setShowEmailTemplatesModal] = useState(false);
  const [showMobileNumberModal, setShowMobileNumberModal] = useState(false);
  const { user } = useAuth();
  const { isSuperAdmin } = useSuperAdmin();
  const { isAdmin } = useAdmin();
  const canSaveMobileForMfa = isAdmin || isSuperAdmin;

  const getUserDisplayInfo = () => {
    if (!user) return { name: 'User', role: 'user' };
    
    const firstName = user.user_metadata?.first_name || '';
    const lastName = user.user_metadata?.last_name || '';
    const role = user.user_metadata?.role || 'user';
    
    let displayName = '';
    if (firstName && lastName) {
      displayName = `${firstName} ${lastName}`;
    } else if (firstName) {
      displayName = firstName;
    } else if (lastName) {
      displayName = lastName;
    } else {
      const email = user.email || '';
      displayName = email.split('@')[0] || 'User';
    }
    
    return { name: displayName, role: role.charAt(0).toUpperCase() + role.slice(1) };
  };

  const { name, role } = getUserDisplayInfo();

  const onRestoreData = () => {
    setShowRestoreModal(true);
    setIsOpen(false);
  };

  const onManageEmailTemplates = () => {
    setShowEmailTemplatesModal(true);
  };

  const onManageMobileNumber = () => {
    setShowMobileNumberModal(true);
    setIsOpen(false);
  };

  const handleBackupData = async () => {
    try {
      // Import all storage services
      const {
        supabaseCustomerStorage,
        supabaseLocationStorage,
        supabaseContactStorage,
        supabaseReminderStorage,
        supabaseReviewStorage,
        supabaseEmailTemplateStorage,
        supabaseBookingStorage,
        supabaseBookingDatesStorage
      } = await import('../../services/supabaseStorage');
      
      // Get all data from Supabase
      const [customers, reminders, reviewReminders, emailTemplates, bookings, bookingDates] = await Promise.all([
        supabaseCustomerStorage.getCustomers(),
        supabaseReminderStorage.getReminders(),
        supabaseReviewStorage.getReviewReminders(),
        supabaseEmailTemplateStorage.getEmailTemplates(),
        supabaseBookingStorage.getBookings(),
        supabaseBookingDatesStorage.getAllBookingDates()
      ]);

      // Prepare CSV data - matching the restore format exactly
      const customerData = customers.map(customer => ({
        id: customer.id,
        companyName: customer.companyName,
        industry: customer.industry || '',
        generalNotes: customer.generalNotes || '',
        numCertsGenerated: customer.numCertsGenerated,
        isPaid: customer.isPaid,
        lastPaymentDate: customer.lastPaymentDate || '',
        paymentHistory: JSON.stringify(customer.paymentHistory || []),
        currentBatchCertificates: JSON.stringify(customer.currentBatchCertificates || []),
        generatedCertificates: JSON.stringify(customer.generatedCertificates || [])
      }));

      const locationData = customers.flatMap(customer =>
        customer.locations.map(location => ({
          id: location.id,
          customerId: customer.id,
          addressLine1: location.addressLine1,
          addressLine2: location.addressLine2 || '',
          addressLine3: location.addressLine3 || '',
          city: location.city,
          eircodePostcode: location.eircodePostcode || '',
          countyState: location.countyState || '',
          country: location.country,
          locationContactName: location.locationContactName || '',
          locationContactEmail: location.locationContactEmail || '',
          locationContactPhone: location.locationContactPhone || '',
          locationNotes: location.locationNotes || ''
        }))
      );

      const contactData = customers.flatMap(customer =>
        customer.contacts.map(contact => ({
          id: contact.id,
          customerId: customer.id,
          firstName: contact.firstName,
          lastName: contact.lastName,
          role: contact.role || '',
          email: contact.email,
          phoneNumber: contact.phoneNumber || '',
          isBillingContact: contact.isBillingContact,
          isGeneralContact: contact.isGeneralContact,
          contactNotes: contact.contactNotes || ''
        }))
      );

      const reminderData = reminders.map(reminder => ({
        id: reminder.id,
        customerId: reminder.customerId,
        companyName: reminder.companyName,
        attendeeName: reminder.attendeeName,
        courseName: reminder.courseName,
        courseDate: reminder.courseDate,
        expiryDate: reminder.expiryDate,
        isRenewed: reminder.isRenewed,
        markedRenewedDate: reminder.markedRenewedDate || '',
        lastReminderSent: reminder.lastReminderSent || ''
      }));

      const reviewReminderData = reviewReminders.map(reminder => ({
        id: reminder.id,
        customerId: reminder.customerId,
        companyName: reminder.companyName,
        paymentDate: reminder.paymentDate,
        dueReminderDate: reminder.dueReminderDate,
        isSent: reminder.isSent,
        isCompleted: reminder.isCompleted,
        lastAttemptDate: reminder.lastAttemptDate || ''
      }));

      const emailTemplateData = emailTemplates.map(template => ({
        id: template.id,
        name: template.name,
        subject: template.subject,
        body: template.body,
        isDefault: template.isDefault
      }));

      const bookingData = bookings.map(booking => ({
        id: booking.id,
        customerId: booking.customerId,
        locationId: booking.locationId || '',
        contactId: booking.contactId || '',
        instructorId: booking.instructorId || '',
        courseName: booking.courseName,
        courseDate: booking.courseDate || '',
        startTime: booking.startTime || '',
        endTime: booking.endTime || '',
        totalPrice: booking.totalPrice || '',
        instructorPrice: booking.instructorPrice || '',
        instructorPO: booking.instructorPO || '',
        attendeeCount: booking.attendeeCount,
        notes: booking.notes || '',
        status: booking.status,
        createdAt: booking.createdAt,
        updatedAt: booking.updatedAt || new Date().toISOString(),
        allBookingDates: []
      }));

      const bookingDatesData = bookingDates.map(bookingDate => ({
        id: bookingDate.id,
        bookingId: bookingDate.bookingId,
        date: bookingDate.date,
        createdAt: bookingDate.createdAt || new Date().toISOString(),
        updatedAt: bookingDate.updatedAt || new Date().toISOString()
      }));

      // Generate CSV content
      const Papa = await import('papaparse');
      
      const csvFiles = {
        'customers.csv': Papa.unparse(customerData),
        'locations.csv': Papa.unparse(locationData),
        'contacts.csv': Papa.unparse(contactData),
        'reminders.csv': Papa.unparse(reminderData),
        'review_reminders.csv': Papa.unparse(reviewReminderData),
        'email_templates.csv': Papa.unparse(emailTemplateData),
        'bookings.csv': Papa.unparse(bookingData),
        'booking_dates.csv': Papa.unparse(bookingDatesData)
      };

      // Create ZIP file
      const JSZip = (await import('jszip')).default;
      const zip = new JSZip();
      
      Object.entries(csvFiles).forEach(([filename, content]) => {
        zip.file(filename, content);
      });

      // Add metadata file
      const metadata = {
        exported_at: new Date().toISOString(),
        exported_by: 'Rapid Cert System',
        version: '1.0',
        total_records: {
          customers: customerData.length,
          locations: locationData.length,
          contacts: contactData.length,
          reminders: reminderData.length,
          review_reminders: reviewReminderData.length,
          email_templates: emailTemplateData.length,
          bookings: bookingData.length,
          booking_dates: bookingDatesData.length
        }
      };
      
      zip.file('metadata.json', JSON.stringify(metadata, null, 2));

      // Generate and download ZIP
      const content = await zip.generateAsync({ type: 'blob' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(content);
      const timestamp = new Date().toISOString().split('T')[0];
      
      link.setAttribute('href', url);
      link.setAttribute('download', `rapid_cert_backup_${timestamp}.zip`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      onBackupData(); // Call the original backup function
    } catch (error) {
      console.error('Error creating backup:', error);
      // Still call the original backup function as fallback
      onBackupData();
    }
  };

  if (collapsed) {
    return (
      <>
        <div className="relative">
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="w-full p-3 flex items-center justify-center hover:bg-slate-100 rounded-xl transition-colors duration-200"
            title={name}
          >
            <div className="w-8 h-8 bg-gradient-to-br from-slate-600 to-slate-700 rounded-full flex items-center justify-center relative">
              <User className="w-4 h-4 text-white" />
              {isSuperAdmin && (
                <Crown className="absolute -top-1 -right-1 w-3 h-3 text-yellow-500" />
              )}
            </div>
          </button>

          {/* Collapsed Dropdown Menu */}
          {isOpen && (
            <>
              <div 
                className="fixed inset-0 z-10" 
                onClick={() => setIsOpen(false)}
              />
              <div className="absolute bottom-full left-16 mb-2 w-64 bg-white rounded-2xl shadow-2xl border border-slate-200 z-20 overflow-hidden">
                <div className="p-4 border-b border-slate-100">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-slate-600 to-slate-700 rounded-full flex items-center justify-center relative">
                      <User className="w-5 h-5 text-white" />
                      {isSuperAdmin && (
                        <Crown className="absolute -top-1 -right-1 w-4 h-4 text-yellow-500" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-slate-800 truncate">{name}</div>
                      <div className="text-sm text-slate-600 flex items-center">
                        {isSuperAdmin && <Crown className="w-3 h-3 text-yellow-500 mr-1" />}
                        {role}
                      </div>
                      <div className="text-xs text-slate-500 truncate">{user?.email}</div>
                    </div>
                  </div>
                </div>
                
                <div className="p-2">
                  {canSaveMobileForMfa && (
                    <button
                      onClick={onManageMobileNumber}
                      className="w-full flex items-center px-4 py-3 text-left hover:bg-teal-50 rounded-xl transition-colors duration-200"
                    >
                      <Phone className="w-4 h-4 mr-3 text-teal-500" />
                      <div>
                        <div className="font-medium text-slate-800">Mobile number</div>
                        <div className="text-xs text-slate-500">For Phone MFA setup</div>
                      </div>
                    </button>
                  )}

                  <button
                    onClick={onManageEmailTemplates}
                    className="w-full flex items-center px-4 py-3 text-left hover:bg-emerald-50 rounded-xl transition-colors duration-200"
                  >
                    <Mail className="w-4 h-4 mr-3 text-emerald-500" />
                    <div>
                      <div className="font-medium text-slate-800">Email Templates</div>
                      <div className="text-xs text-slate-500">Manage email templates</div>
                    </div>
                  </button>
                  
                  <button
                    onClick={() => {
                      handleBackupData();
                      setIsOpen(false);
                    }}
                    className="w-full flex items-center px-3 py-2 text-left hover:bg-slate-50 rounded-lg transition-colors duration-200"
                  >
                    <Download className="w-4 h-4 mr-3 text-blue-500" />
                    <span className="text-sm font-medium text-slate-800">Backup Data</span>
                  </button>
                  
                  <button
                    onClick={() => {
                      onSignOut();
                      setIsOpen(false);
                    }}
                    className="w-full flex items-center px-3 py-2 text-left hover:bg-red-50 rounded-lg transition-colors duration-200"
                  >
                    <LogOut className="w-4 h-4 mr-3 text-red-500" />
                    <span className="text-sm font-medium text-slate-800">Sign Out</span>
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
        
        {/* Modals */}
        {showRestoreModal && (
          <DataRestoreModal
            isOpen={showRestoreModal}
            onClose={() => setShowRestoreModal(false)}
            onDataRestored={() => {
              setShowRestoreModal(false);
            }}
          />
        )}
        
        {showEmailTemplatesModal && (
          <ManageEmailTemplatesModal
            onClose={() => setShowEmailTemplatesModal(false)}
            onTemplatesUpdated={() => {
              // Handle template updates if needed
            }}
          />
        )}

        {showMobileNumberModal && (
          <SaveMobileNumberModal onClose={() => setShowMobileNumberModal(false)} />
        )}
      </>
    );
  }

  return (
    <>
      <div className="relative">
        {/* User Info */}
        <div className="bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-800 dark:to-slate-900 rounded-xl p-3 border border-slate-200 dark:border-slate-700">
          <div className="flex items-center space-x-2">
            <div className="w-9 h-9 bg-gradient-to-br from-slate-600 to-slate-700 dark:from-slate-500 dark:to-slate-600 rounded-lg flex items-center justify-center relative shadow-md">
              <User className="w-5 h-5 text-white" />
              {isSuperAdmin && (
                <Crown className="absolute -top-0.5 -right-0.5 w-3 h-3 text-yellow-500" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-sm text-slate-800 dark:text-slate-200 truncate">{name}</div>
              <div className="text-xs text-slate-600 dark:text-slate-300 flex items-center">
                {isSuperAdmin && <Crown className="w-2.5 h-2.5 text-yellow-500 mr-1" />}
                {role}
              </div>
            </div>
          </div>
        </div>

        {/* User Actions */}
        <div className="space-y-1 mt-2">
          {canSaveMobileForMfa && (
            <button
              onClick={onManageMobileNumber}
              className="w-full flex items-center px-3 py-2 text-left hover:bg-teal-50 dark:hover:bg-slate-800 rounded-lg transition-colors duration-200 group"
            >
              <Phone className="w-4 h-4 mr-2 text-teal-500" />
              <span className="text-xs font-medium text-slate-800 dark:text-slate-200">Mobile number</span>
            </button>
          )}

          <button
            onClick={onManageEmailTemplates}
            className="w-full flex items-center px-3 py-2 text-left hover:bg-emerald-50 dark:hover:bg-slate-800 rounded-lg transition-colors duration-200 group"
          >
            <Mail className="w-4 h-4 mr-2 text-emerald-500" />
            <span className="text-xs font-medium text-slate-800 dark:text-slate-200">Email Templates</span>
          </button>
          
          <button
            onClick={handleBackupData}
            className="w-full flex items-center px-3 py-2 text-left hover:bg-slate-100 rounded-lg transition-colors duration-200 group"
          >
            <Download className="w-4 h-4 mr-2 text-blue-500" />
            <span className="text-xs font-medium text-slate-800 dark:text-slate-200">Backup Data</span>
          </button>
          
          <button
            onClick={onRestoreData}
            className="w-full flex items-center px-3 py-2 text-left hover:bg-purple-50 rounded-lg transition-colors duration-200 group"
          >
            <RotateCcw className="w-4 h-4 mr-2 text-purple-500" />
            <span className="text-xs font-medium text-slate-800 dark:text-slate-200">Restore Data</span>
          </button>
          
          <button
            onClick={onSignOut}
            className="w-full flex items-center px-3 py-2 text-left hover:bg-red-50 rounded-lg transition-colors duration-200 group"
          >
            <LogOut className="w-4 h-4 mr-2 text-red-500" />
            <span className="text-xs font-medium text-slate-800 dark:text-slate-200">Sign Out</span>
          </button>
        </div>
      </div>
      
      {/* Modals */}
      {showRestoreModal && (
        <DataRestoreModal
          isOpen={showRestoreModal}
          onClose={() => setShowRestoreModal(false)}
          onDataRestored={() => {
            setShowRestoreModal(false);
          }}
        />
      )}

      {showMobileNumberModal && (
        <SaveMobileNumberModal onClose={() => setShowMobileNumberModal(false)} />
      )}
    </>
  );
}